import React, { useEffect, useMemo, useState, useRef } from 'react';
import { ExternalLink } from 'lucide-react';
import { useLocation, Navigate, useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/PageHeader';
import IntelAxisTablePagination from '../../../components/mi/IntelAxisTablePagination';
import { getMarketingTableOptions, listMarketingAdsTable } from '../../../api/nexus';
import { assignFilterArray } from '../../../utils/filtersApi';
import AdCreativeThumbnail from '../../../components/mi/AdCreativeThumbnail';
import MiTableFiltersDrawer from '../../../components/mi/MiTableFiltersDrawer';
import TableHoverTooltip from '../../../components/mi/TableHoverTooltip';
import { MarketingPlatformTd } from '../../../components/mi/MarketingPlatformCell';
import { formatTableDate, formatTableValue } from '../../../utils/intelAxisTableUtils';

const platformFromPath = (pathname) => {
  if (pathname.endsWith('/meta')) return 'meta';
  if (pathname.endsWith('/google')) return 'google';
  return 'linkedin';
};

const platformLabel = (platform) => {
  if (platform === 'meta') return 'Meta';
  if (platform === 'google') return 'Google';
  return 'LinkedIn';
};

const formatMediaLabel = (mediaType) => {
  const raw = String(mediaType || '').trim();
  if (!raw) return '—';
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
};

export default function MarketingTable() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMarketingRoot = location.pathname === '/marketing';
  const platform = platformFromPath(location.pathname);

  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCompany, setFilterCompany] = useState('All companies');
  const [filterMediaType, setFilterMediaType] = useState('All media types');
  const [showTableFilters, setShowTableFilters] = useState(false);
  const [options, setOptions] = useState({
    companies: ['All companies'],
    mediaTypes: ['All media types'],
    adVideoPlaybackEnabled: false,
  });
  const [selectedId, setSelectedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pageInput, setPageInput] = useState('1');
  const [openFilter, setOpenFilter] = useState(null);
  const [mapTooltip, setMapTooltip] = useState(null);
  const [drawerCreativeReady, setDrawerCreativeReady] = useState(false);

  const lastRowRef = useRef(null);
  const currentRow = useMemo(() => rows.find((r) => r.id === selectedId), [rows, selectedId]);
  if (currentRow) {
    lastRowRef.current = currentRow;
  }
  const selectedRow = currentRow || lastRowRef.current;

  const companyCatalog = useMemo(
    () => options.companies.filter((name) => name && name !== 'All companies'),
    [options.companies],
  );

  const mediaTypeCatalog = useMemo(
    () => options.mediaTypes.filter((name) => name && name !== 'All media types'),
    [options.mediaTypes],
  );

  useEffect(() => {
    setSelectedId(null);
    setCurrentPage(1);
    setFilterCompany('All companies');
    setFilterMediaType('All media types');
  }, [platform]);

  useEffect(() => {
    if (!selectedId) {
      setDrawerCreativeReady(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setDrawerCreativeReady(true), 380);
    return () => window.clearTimeout(timer);
  }, [selectedId]);

  useEffect(() => {
    getMarketingTableOptions(platform)
      .then((res) => {
        const data = res?.data?.data || {};
        setOptions({
          companies: data.companies || ['All companies'],
          mediaTypes: data.mediaTypes || ['All media types'],
          adVideoPlaybackEnabled: Boolean(data.adVideoPlaybackEnabled),
        });
        setFilterMediaType('All media types');
      })
      .catch((err) => console.error('[marketing] options failed', err));
  }, [platform]);

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const body = {
      page: currentPage,
      limit: rowsPerPage,
      platform,
      ...(searchTerm ? { search: searchTerm } : {}),
    };
    assignFilterArray(body, 'company', filterCompany, 'All companies', companyCatalog);
    assignFilterArray(body, 'mediaType', filterMediaType, 'All media types', mediaTypeCatalog);

    const timer = setTimeout(() => {
      listMarketingAdsTable(body)
        .then((res) => {
          if (cancelled) return;
          setRows(res?.data?.data || []);
          setTotalRows(res?.data?.meta?.totalItems || 0);
        })
        .catch((err) => {
          console.error('[marketing] list failed', err);
          if (!cancelled) {
            setRows([]);
            setTotalRows(0);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    currentPage,
    rowsPerPage,
    searchTerm,
    filterCompany,
    filterMediaType,
    platform,
    companyCatalog,
    mediaTypeCatalog,
  ]);

  const hasFiltersApplied = filterCompany !== 'All companies'
    || filterMediaType !== 'All media types'
    || Boolean(searchTerm.trim());

  const handlePageInputSubmit = (e) => {
    e.preventDefault();
    const next = parseInt(pageInput, 10);
    const totalPages = Math.ceil(totalRows / rowsPerPage) || 1;
    if (Number.isFinite(next) && next >= 1 && next <= totalPages) {
      setCurrentPage(next);
    } else {
      setPageInput(String(currentPage));
    }
  };

  if (isMarketingRoot) {
    return <Navigate to="/marketing/linkedin" replace />;
  }

  if (loading && rows.length === 0) {
    return <div className="content">Loading {platformLabel(platform)} ads…</div>;
  }

  return (
    <>
      <PageHeader
        title={`Marketing - ${platformLabel(platform)} Ads`}
        activePageTab="Table"
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setCurrentPage(1);
        }}
        placeholder="Search ad ID, headline, company…"
        hasFiltersApplied={hasFiltersApplied}
        onClearFilters={() => {
          setSearchTerm('');
          setFilterCompany('All companies');
          setFilterMediaType('All media types');
          setCurrentPage(1);
        }}
        showTableFilters={showTableFilters}
        onToggleFilters={() => setShowTableFilters(!showTableFilters)}
      />

      <div className="body">
        <div className="table-area filings-table-panel">
          <div className="table-wrap" style={{ padding: '0 16px' }}>
            <table style={{ tableLayout: 'fixed', minWidth: '1020px' }}>
              <thead>
                <tr>
                  <th className="col-company" style={{ width: '14%' }}><div className="th-sort">Company</div></th>
                  <th className="col-product-name" style={{ width: '28%' }}>Headline</th>
                  <th className="col-creative" style={{ width: '10%' }}>Creative</th>
                  <th className="col-platform" style={{ width: '10%' }}>Platform</th>
                  <th className="col-type" style={{ width: '10%' }}>Media</th>
                  <th className="col-status" style={{ width: '10%' }}>Status</th>
                  <th className="col-date" style={{ width: '12%' }}><div className="th-sort">Last seen</div></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: 'var(--text3)' }}>
                      No {platformLabel(platform)} ads match your filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, rowIdx) => (
                    <tr
                      key={row.id}
                      className={selectedId === row.id ? 'selected' : ''}
                      style={{ '--table-row-delay': rowIdx }}
                      onClick={() => setSelectedId(row.id)}
                    >
                      <td className="company-cell">
                        <div className="company-name">
                          {formatTableValue(row.company, 28, setMapTooltip)}
                        </div>
                      </td>
                      <td>{formatTableValue(row.title, 50, setMapTooltip)}</td>
                      <td>
                        <div className="marketing-table-thumb">
                          <AdCreativeThumbnail
                            ad={row}
                            playbackEnabled={false}
                            eager={rowIdx < 5}
                          />
                        </div>
                      </td>
                      <MarketingPlatformTd row={row} routePlatform={platform} />
                      <td>{formatMediaLabel(row.mediaType)}</td>
                      <td>
                        <span className={`pill ${row.isActive ? 'pill-green' : 'pill-amber'}`}>
                          {row.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="date-cell">{formatTableDate(row.dateLastSeen)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <IntelAxisTablePagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            totalRows={totalRows}
            entityLabel="ads"
            pageInput={pageInput}
            setPageInput={setPageInput}
            onPageInputSubmit={handlePageInputSubmit}
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            filterKey="marketing-rows"
          />
        </div>

        <div className={`drawer ${selectedId ? 'drawer-open' : ''}`}>
          {selectedRow ? (
            <>
              <div className="drawer-header">
                <div>
                  {/* <div className="drawer-tn">{selectedRow.adId}</div> */}
                  <div className="drawer-company">{selectedRow.company}</div>
                  <div className="drawer-toi">
                    {platformLabel(platform)} · {formatMediaLabel(selectedRow.mediaType)}
                  </div>
                </div>
                <button
                  type="button"
                  className="drawer-close filters-drawer-close"
                  onClick={() => setSelectedId(null)}
                >
                  ×
                </button>
              </div>
              <div className="drawer-body">
                <div className="social-post-card" style={{ marginBottom: '24px' }}>
                  <div className="social-post-header">
                    <div className="social-post-avatar">
                      {selectedRow.company ? selectedRow.company.charAt(0).toUpperCase() : 'A'}
                    </div>
                    <div className="social-post-header-text">
                      <div className="social-post-company">{selectedRow.pageName || selectedRow.company || 'Unknown'}</div>
                      <div className="social-post-sponsored">Sponsored</div>
                    </div>
                  </div>

                  {selectedRow.adCopy ? (
                    <div className="social-post-body">
                      {selectedRow.adCopy}
                    </div>
                  ) : null}

                  <div className="social-post-creative">
                    {selectedRow.mediaUrl || selectedRow.videoPlaybackUrl ? (
                      drawerCreativeReady ? (
                        <AdCreativeThumbnail
                          key={selectedRow.id}
                          mountKey={String(selectedRow.id)}
                          ad={selectedRow}
                          playbackEnabled={options.adVideoPlaybackEnabled}
                          eager
                          imageClassName="social-post-image"
                          fallbackClassName="social-post-image"
                        />
                      ) : (
                        <div
                          className={`marketing-drawer-creative marketing-drawer-creative--placeholder marketing-drawer-creative--${platform}`}
                          aria-hidden
                        />
                      )
                    ) : (
                      <div className="mi-detail-creative-empty">No preview available for this ad.</div>
                    )}
                  </div>

                  <div className="social-post-footer">
                    <div className="social-post-footer-text">
                      <div className="social-post-footer-domain">{platformLabel(platform).toUpperCase()}</div>
                      <div className="social-post-footer-title">{selectedRow.headline || selectedRow.title || 'Ad creative'}</div>
                    </div>
                    {selectedRow.ctaLink ? (
                      <a
                        className="btn btn-sm"
                        style={{ background: '#e4e6eb', border: 'none', color: '#050505', fontWeight: 600, padding: '8px 16px', whiteSpace: 'nowrap', flexShrink: 0 }}
                        href={selectedRow.ctaLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {selectedRow.ctaText || 'Learn More'}
                      </a>
                    ) : null}
                  </div>
                </div>
                <div className="detail-section">
                  <div className="ds-title">Activity</div>
                  <div className="field-grid">
                    <div className="field">
                      <div className="field-label">Status</div>
                      <div className="field-val">{selectedRow.isActive ? 'Active' : 'Inactive'}</div>
                    </div>
                    <div className="field">
                      <div className="field-label">Last seen</div>
                      <div className="field-val">{selectedRow.lastSeenLabel || formatTableDate(selectedRow.dateLastSeen)}</div>
                    </div>
                    <div className="field">
                      <div className="field-label">First seen</div>
                      <div className="field-val">{formatTableDate(selectedRow.dateFirstSeen)}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="drawer-actions">
                {selectedRow.adLibraryUrl ? (
                  <a
                    className="btn btn-sm"
                    href={selectedRow.adLibraryUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ background: '#fff', border: '1px solid var(--border)', color: 'var(--c-black)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', textDecoration: 'none' }}
                  >
                     Visit Ad library <ExternalLink size={14} />
                  </a>
                ) : null}
                <button
                  type="button"
                  className="btn btn-sm btn-filters"
                  onClick={(e) => {
                    e.stopPropagation();
                    const adPlatform = selectedRow.platform || platform;
                    sessionStorage.setItem('comply_last_marketing_path', location.pathname);
                    navigate(`/marketing/${adPlatform}/${selectedRow.id}`);
                  }}
                >
                  More info
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <TableHoverTooltip tooltip={mapTooltip} />

      <MiTableFiltersDrawer
        isOpen={showTableFilters}
        onClose={() => setShowTableFilters(false)}
        filterCompany={filterCompany}
        setFilterCompany={setFilterCompany}
        companyCatalog={companyCatalog}
        filterMediaType={filterMediaType}
        setFilterMediaType={setFilterMediaType}
        mediaTypeCatalog={mediaTypeCatalog}
        showMediaType
        setCurrentPage={setCurrentPage}
      />
    </>
  );
}
